/**
 * Reine Logik des Dev-Menüs (ohne React, ohne Server): Sicht-Abbildung, Personenfilter, Kopfzeilen-Text,
 * Ablehnung gegen Fehlschlag. Getrennt, damit sie direkt testbar ist und die Oberfläche nur noch zeigt.
 */

/**
 * Sicht der Person (Betreiber 13.10.2026, W100 a): Entwicklersicht (gespeichert „dev“) zeigt die Entwicklungs-Hilfen,
 * Nutzersicht (gespeichert „prod“) blendet sie alle aus, so wie die Seite für Anwender aussieht. Reine Anzeige:
 * Rechte und Daten bleiben unverändert.
 */
export type DevSicht = "entwickler" | "nutzer";

/** Das gespeicherte Drei-Werte-Schema (`devMode` der Nutzer-Einstellungen) auf die Zwei-Werte-Oberfläche. */
export function sichtAusGespeichert(devMode: string | null | undefined): DevSicht {
  return devMode === "dev" ? "entwickler" : "nutzer";
}

/** Umgekehrt: Entwicklersicht → „dev“, Nutzersicht → „prod“ (das Schema der Route bleibt unverändert). */
export function gespeichertAusSicht(sicht: DevSicht): "dev" | "prod" {
  return sicht === "entwickler" ? "dev" : "prod";
}

/** Sätze der Sicht (eine Quelle für Oberfläche und Test). */
export const DEV_SICHT_SATZ = "Nutzersicht blendet alle Entwicklungs-Hilfen aus, so wie die Seite für Anwender aussieht.";
export const DEV_KI_VORSCHAU_SATZ = "Zeigt die Seiten so, wie sie ohne KI aussehen.";
export const DEV_KI_STREIFEN_SATZ = "KI-Vorschau aus";

/** Person in der Liste „Ansicht als“ (Form der Server-Action `listImpersonatableUsers`). `allowed` kommt aus der Server-Regel `canImpersonate` (eine Quelle, r110). */
export interface DevMenuePerson {
  id: string;
  name: string | null;
  email: string;
  allowed: boolean;
}

/** Anzeigename: Name, sonst Adresse. */
export function personAnzeige(p: { name: string | null; email: string }): string {
  return p.name?.trim() ? p.name : p.email;
}

/** Teilwort-Filter über Name und Adresse, Groß-/Kleinschreibung egal; leere Suche zeigt alle. */
export function filterePersonen(personen: readonly DevMenuePerson[], suche: string): DevMenuePerson[] {
  const q = suche.trim().toLowerCase();
  if (!q) return [...personen];
  return personen.filter((p) => `${p.name ?? ""} ${p.email}`.toLowerCase().includes(q));
}

/** Text der Pille: „DEV“, bei laufender Impersonation „DEV · Anna B.“ (Name gekürzt zur Anzeige im Auslöser). */
export function pillenText(label: string, impersoniertAls: string | null): string {
  const name = impersoniertAls?.trim();
  return name ? `${label} · ${name}` : label;
}

/** Der feste Ablehnungssatz der Dev-Werkzeuge (Server: `DEV_WERKZEUGE_ABGELEHNT`); nur er gilt als Ablehnung, alles andere als Fehlschlag. */
export const DEV_ABGELEHNT_SATZ = "Dieses Werkzeug steht nicht zur Verfügung.";
export const DEV_FEHLSCHLAG_WECHSEL = "Der Wechsel konnte nicht ausgeführt werden. Bitte erneut versuchen.";
export const DEV_FEHLSCHLAG_LISTE = "Die Personenliste konnte nicht geladen werden. Bitte erneut versuchen.";
export const DEV_FEHLSCHLAG_ERZEUGEN = "Der Test-Nutzer konnte nicht erzeugt werden. Bitte erneut versuchen.";
export const DEV_FEHLSCHLAG_SCHALTER = "Die Einstellung konnte nicht gespeichert werden. Bitte erneut versuchen.";

/**
 * Ablehnung (übers Ziel, endgültig) und Fehlschlag (über den Vorgang, wiederholbar) sind nie derselbe Satz (r105).
 * Eine geworfene Meldung zählt nur dann als Ablehnung, wenn sie genau der feste Satz ist; Next versteckt in Produktion
 * geworfene Texte, dort bleibt es beim wiederholbaren Satz ohne Code.
 */
export function satzFuerFehler(fehler: unknown, fehlschlagSatz: string): string {
  return fehler instanceof Error && fehler.message === DEV_ABGELEHNT_SATZ ? DEV_ABGELEHNT_SATZ : fehlschlagSatz;
}

/** Ist der Tastendruck das Kürzel des Menüs (Strg+Umschalt+D)? Mit Alt oder Meta nie. */
export function istMenueKuerzel(e: { key: string; ctrlKey: boolean; shiftKey: boolean; altKey: boolean; metaKey: boolean }): boolean {
  return e.ctrlKey && e.shiftKey && !e.altKey && !e.metaKey && e.key.toLowerCase() === "d";
}
